import styled from 'styled-components';

import ModalContainer from '../../containers/ModalContainer';
import { Typography } from '../../../display/Typography';
import { MainButton } from '../../../inputs/buttons/MainButton';
// Selector de componente para acotar el ancho del campo de fecha SOLO dentro
// de este modal (ver DatesGrid). No se re-estiliza el DateTime en su fichero.
import { InputDate } from '../../../inputs/DateTime/styles';

// Cargando canónico (patrón ConfirmTransferModal/DeleteTeamModal/PauseTimer):
// overlay Loader absoluto + contenido atenuado mientras isLoading.
export const ModalContainerStyled = styled(ModalContainer)`
  position: relative;
`;

export const ContainerLoader = styled.div`
  position: absolute;
  top: 40%;
  left: 45%;
  z-index: 1;
`;

export const ContainerForm = styled.form<{ isLoading?: boolean }>`
  opacity: ${(props) => (props.isLoading ? 0.6 : 1)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  /* Padding lateral 3.5rem y no 6.35 (fix 2026-08-10): los 6.35rem son los que
     usan sus hermanos del ciclo de patrulla, que tienen 2-3 campos; este modal
     mete PK×2, sentido, carril, DOS fechas con hora y la descripción, y ese
     margen se comía justo el ancho que necesitaba la fila de fechas. Libera
     ~5.7rem sin tocar el ancho del modal (60rem, igual que sus hermanos). */
  padding: 2.5rem 3.5rem;
  width: 100%;
  max-height: 92vh;
  overflow-y: auto;

  /* Mismo padding/gap comprimidos que ChangeDirectionModal en tablet: este
     modal tiene más campos que sus hermanos, así que es el que más necesita
     el alto y el ancho que libera (feedback usuario 2026-08-05). */
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    padding: 1.5rem 2rem;
    gap: 1.25rem;
  }
`;

export const Text = styled(Typography)`
  text-align: center;
`;

export const Title = styled(Text)`
  font-size: 2.4rem;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spaces.xs}rem;
  width: 100%;
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.m};
  width: 100%;

  /* Separación EXTRA respecto a la fila de fechas que tiene justo encima
     (feedback usuario 2026-08-05). Es un modal de tablet y se usa con el dedo:
     con solo el gap del formulario, "Guardar cambios" queda a un pelo del
     campo "Hora final" y un dedo grande dispara el guardado creyendo que va a
     editar la hora. Aquí el error no es recuperable de un vistazo — el modal
     se cierra y el tramo queda guardado. */
  margin-top: ${({ theme }) => theme.spaces.m}rem;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    /* Botones SIEMPRE en fila (uno al lado del otro), también en tablet/móvil:
       apilarlos en columna alargaba el modal y se cortaba en navegador (sin la
       barra extra de la PWA). En pantallas estrechas comparten el ancho.
       Misma pauta que ChangeDirectionModal (feedback usuario 2026-08-05). */
    & > * {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

export const ActionButton = styled(MainButton)`
  min-width: 13.4rem;
  justify-content: center;

  /* Tablet/móvil: botones EN FILA compartiendo ancho (ver ButtonRow). Sin
     bajar este min-width no cabían dos y se apilaban → modal más alto. */
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    min-width: 0;
    flex: 1 1 0;
  }
`;

// El par PK inicial / PK final va SIEMPRE en dos columnas, también en tablet
// vertical (feedback usuario 2026-08-05). Son dos campos cortos ("207 + 000",
// 17rem cada uno): apilarlos solo alargaba el modal, que es justo el problema
// que hacía que no cupiera. Dos × 17rem + gap entran de sobra en el ancho útil
// del cuerpo incluso con el modal recortado a 95vw.
export const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spaces.s}rem;
  width: 100%;
`;

/**
 * Las dos fechas (inicio y fin, cada una con su hora) en la MISMA fila.
 *
 * Dos DateTime tal cual miden ~45rem y no cabían: por eso el modal se
 * desbordaba a lo ancho al principio, y por eso luego iban apiladas, lo que
 * dejaba el modal demasiado alto para la tablet vertical. La salida (idea del
 * usuario, 2026-08-05) es recortar el sobrante del campo de fecha en vez de
 * gastar alto.
 *
 * `auto-fit` + `minmax` en lugar de una media query: si las dos columnas caben
 * se ponen en fila y si no se apilan solas, sin depender de un punto de
 * ruptura — el ancho del que disponemos no lo marca la pantalla sino el modal
 * (60rem topados a 95vw), así que un breakpoint por viewport mentiría.
 */
export const DatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(21rem, 1fr));
  gap: ${({ theme }) => theme.spaces.s}rem;
  width: 100%;

  /* El campo de fecha del DateTime mide 15rem FIJOS, dimensionado para
     formularios a ancho completo. Aquí van dos DateTime (fecha + hora cada
     uno) en la misma fila dentro de un modal de ancho fijo, así que hay que
     acotarlo — pero SOLO dentro de este contenedor: el DateTime lo comparten
     decenas de pantallas y estrecharlo en su fichero las tocaría todas.

     13rem y no 11 (fix 2026-08-10, reportado por el usuario): con 11rem
     "06/08/2026" se CORTABA a media fecha. El hueco extra sale de aflojar el
     padding lateral del formulario (ver ContainerForm), no de estrechar más
     nada. */
  ${InputDate} {
    width: 13rem;
  }

  /* Sin esto las dos columnas se negaban a encogerse (el mínimo automático de
     una celda de grid es 'auto') y su contenido se DESBORDABA una sobre otra:
     las etiquetas del DateTime llevan white-space: nowrap, así que no partían
     — se pisaban, y se leía "Hora iniciḞin del tramo". */
  > * {
    min-width: 0;
  }
`;

/* Descripción del tramo — mismo wrap que PauseSurveillanceRouteModal, para que
   el campo se vea igual en el modal que la escribe y en el que la corrige. */
export const TextAreaWrap = styled.div`
  width: 100%;

  & > div {
    width: 100%;
  }

  & textarea {
    min-height: 6rem;
  }
`;

/* El PK es UN valor corto ("207+000"): no debe ocupar todo el ancho de su
   columna. 17rem = mismo ancho EXACTO que el PkFieldWrap de ChangeDirectionModal
   y NewSurveillanceSegmentModal, para que el campo se vea igual en los cuatro
   modales del ciclo de patrulla. Hace falta el wrap porque el InputText fuerza
   max-width:100% en tablet/portrait. */
export const PkFieldWrap = styled.div`
  width: 100%;
  max-width: 17rem;
`;

// Aviso de validación local (espejo de las reglas del backend). Rojo y
// centrado, encima de los botones.
export const InlineError = styled(Text)`
  color: ${({ theme }) => theme.colors.garnet};
`;

/* Aviso de que guardar con una sola carretera borra el cruce registrado.
   Mismo aspecto que el de cambiar de carretera en el asistente
   (`UbicacioTab/styles.ts`): es la misma clase de mensaje —no bloquea, avisa de
   una consecuencia— y no debería leerse de dos formas distintas. */
export const CrossingNotice = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;

  margin: 0.4rem 0 0;
  padding: 0.8rem 1.2rem;

  border: 1px solid ${({ theme }) => theme.colors.secondaryOrange};
  border-radius: 0.6rem;
  background-color: ${({ theme }) => theme.colors.orangeTransparent};

  color: ${({ theme }) => theme.colors.secondaryOrange};
  font-size: 1.4rem;
  line-height: 1.4;
`;

/** El símbolo, para que no se lea como parte de la frase. */
export const CrossingNoticeIcon = styled.span`
  flex: none;
  font-size: 1.6rem;
  line-height: 1.2;
`;
